import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AgGrid,
  Button,
  Checkbox,
  Grid,
  HelpIndicator,
  ModalV2,
  RadioBar,
  Select,
  Tag,
  TextInput,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { useFeatureFlag } from "@jsq/flags-react";
import {
  clientIdKeyFor,
  referenceAncestorKeys,
  referenceDescendantKeys,
  sortGlobalsByRolePriority,
} from "#common/attributeKeys";
import {
  attributeNameConflictMessage,
  mintAttributeSlug,
  newAttributeSlugSuffix,
  slugifyAttributeName as slugify,
} from "#common/attributeNameConflicts";
import {
  DUPLICATE_VALUE_MESSAGE,
  isDuplicateAttributeValue,
  SAVED_VALUE_SET_LOCK_MESSAGE,
} from "#common/attributeValues";
import { ReadOnlyChip } from "#common/components/ReadOnlyChip";
import { useAttributeNameConflict } from "#common/hooks/useAttributeNameConflict";
import { ClientPicker } from "./ClientPicker";
import { ClientScopeHelper, ClientScopePanel } from "./ClientScopePanel";
import { ConvertToAllClientsModal } from "./ConvertToAllClientsModal";
import { clientIdRequiredErrorMessage, isDuplicateSpaceNameError } from "./errors";
import * as styles from "./styles_vanilla.css";
import type {
  AttrDataType,
  GlobalAttribute,
  GlobalRef,
  Space,
  SpaceContract,
  SpaceLocalAttribute,
} from "#common/types/vault";
import type { SpaceEditorProps } from "./types";

export type { SpaceEditorProps };

const EDITABLE_DATATYPES: AttrDataType[] = [
  "Text",
  "Number",
  "Date",
  "Yes/No",
  "Single-select",
  "Multi-select",
];
const isSelectType = (dt: AttrDataType) => dt === "Single-select" || dt === "Multi-select";

const DEFAULT_CONTRACT: SpaceContract = {
  permission: "",
  quality: "guaranteed",
  qualityNote: "",
  lifecycle: "",
  ingest: "Manual",
  channel: null,
};

const DOMAIN_TAG_TOOLTIP =
  "Domain is a filter tag, not a wall — this space stays in the one queryable universe.";

type Draft = {
  name: string;
  domain: string;
  clientScope: "firm" | "specific";
  /** Bare arena pks — what gets saved. */
  clients: string[];
  /**
   * The same selection as relay GlobalIDs, kept only so `ClientPicker` can
   * render its chips (see its module header). Never persisted; a space loaded
   * from the API has no GlobalIDs, so this starts empty on edit.
   */
  clientGlobalIds: string[];
  /**
   * A confirmed-but-unsaved conversion to all-clients (DV-501). Staged rather
   * than applied so the takeover's Save is the only write; until then the space
   * is untouched and the user can undo.
   */
  convertToAllClients: boolean;
  roleScope: "all" | "specific";
  roles: string[];
  contract: SpaceContract;
  globals: Record<string, GlobalRef>;
  /**
   * Requirement choices temporarily overridden by the reference hierarchy.
   * Restored when the key no longer has a referenced descendant.
   */
  referenceRequirementPreForce: Record<string, boolean>;
  /**
   * The client attribute's assignment as it stood before the client-scope pin
   * overwrote it, or `null` while nothing is pinned (DV-684).
   *
   * Kept so releasing the pin can put it back. The Requirement cell is locked
   * while pinned, so `required: true` there is never a value the user chose —
   * leaving it behind on release silently changed the space's schema, and the
   * note that justified it disappears with the pin, so nothing on screen said so.
   * Draft-only: `save` builds its payload field by field and never sees this.
   */
  clientIdPrePin: GlobalRef | null;
  locals: SpaceLocalAttribute[];
};

/**
 * Which way the Client scope radio opens for a saved space.
 *
 * Read from `appliesToAllClients`, NOT from whether the client list happens to
 * be non-empty: a space scoped to specific clients that has none listed yet is
 * still "One or more clients" (DV-501). Deriving it from `clients.length` made
 * that space open on "All clients" while `ClientScopePanel` — which is gated on
 * the flag — treated it as scoped, so the radio contradicted the panel and the
 * convert confirmation was unreachable (the radio already read "firm", and the
 * scoped option is disabled, so there was no click that could stage it).
 *
 * The list shape is only a fallback for a space whose flag is unknown — drafts,
 * mocks and fixtures that never came from the API.
 */
const clientScopeFor = (source: Space | null): Draft["clientScope"] => {
  if (source?.appliesToAllClients !== undefined)
    return source.appliesToAllClients ? "firm" : "specific";
  return source?.clients?.length ? "specific" : "firm";
};

/**
 * DV-684 — force Client ID on and Required, the state DV-683 demands of every
 * client-scoped space. A space scoped to specific clients whose documents carry
 * no Client ID value cannot attribute them to a client, so the backend refuses
 * the write; the pin makes that state unreachable instead of only unsaveable.
 *
 * Applied wherever the draft becomes client-scoped, including on mount: DV-683
 * enforces on writes only and backfills nothing, so a space saved before it
 * landed can still open here without the assignment.
 */
const pinClientId = (
  globals: Record<string, GlobalRef>,
  clientIdKey: string,
  globalLib: readonly GlobalAttribute[],
): Record<string, GlobalRef> => {
  // `clientIdKeyFor` falls back to its first alias when the catalog carries no
  // Client ID attribute at all. Pinning on that key would write a phantom entry
  // the grid never renders (`refGlobals` filters on `globalLib`) while the note
  // named a row that does not exist — so there is nothing to pin here.
  if (!clientIdInCatalog(globalLib, clientIdKey)) return globals;
  const current = globals[clientIdKey] as GlobalRef | undefined;
  return {
    ...globals,
    [clientIdKey]: { on: true, required: true, default: current?.default ?? "" },
  };
};

/** Whether the catalog actually carries the row `clientIdKey` names — see {@link pinClientId}. */
const clientIdInCatalog = (globalLib: readonly GlobalAttribute[], clientIdKey: string) =>
  globalLib.some((a) => a.key === clientIdKey);

/**
 * Force Required onto every referenced ancestor of a referenced attribute.
 *
 * Only ancestors that are themselves referenced are touched: a missing ancestor
 * is an invalid space, reported by {@link referenceHierarchyError}, not something
 * this editor invents a reference for.
 *
 * Applied to edits made in this session, NOT on open — see {@link buildDraft}.
 */
const reconcileReferenceHierarchyRequirements = (
  globals: Record<string, GlobalRef>,
  preForce: Record<string, boolean>,
  globalLib: readonly GlobalAttribute[],
): Pick<Draft, "globals" | "referenceRequirementPreForce"> => {
  const requiredAncestors = new Set<string>();

  globalLib.forEach(({ key }) => {
    if (!globals[key]?.on) return;
    referenceAncestorKeys(key, globalLib).forEach((ancestorKey) => {
      if (globals[ancestorKey]?.on) requiredAncestors.add(ancestorKey);
    });
  });

  const nextPreForce = { ...preForce };
  const next = { ...globals };
  requiredAncestors.forEach((key) => {
    if (!(key in nextPreForce) && !globals[key]?.required) nextPreForce[key] = false;
    next[key] = { ...next[key], required: true };
  });

  Object.entries(preForce).forEach(([key, required]) => {
    if (requiredAncestors.has(key)) return;
    next[key] = { ...next[key], required };
    delete nextPreForce[key];
  });

  return { globals: next, referenceRequirementPreForce: nextPreForce };
};

const referenceHierarchyError = (
  globals: Record<string, GlobalRef>,
  globalLib: readonly GlobalAttribute[],
): string | null => {
  for (const attribute of globalLib) {
    if (!globals[attribute.key]?.on) continue;
    const missingParents = referenceAncestorKeys(attribute.key, globalLib).filter(
      (key) => !globals[key]?.on,
    );
    if (missingParents.length === 0) continue;
    const names = missingParents.map(
      (key) => globalLib.find((candidate) => candidate.key === key)?.name ?? key,
    );
    return `${attribute.name} requires ${names.join(
      " and ",
    )}. Add the missing parent attributes or remove ${attribute.name} before saving.`;
  }
  return null;
};

/**
 * Put back the assignment the pin overwrote. The inverse of {@link pinClientId}.
 *
 * Releasing used to leave the forced `required: true` in place, reasoning that
 * downgrading it "would undo a deliberate choice" — but the choice was the pin's,
 * not the user's: Requirement is locked for as long as it holds. So a user who
 * went specific → all-clients had silently changed the space's schema, with the
 * note that explained the value gone in the same click.
 */
const releaseClientIdPin = (
  globals: Record<string, GlobalRef>,
  clientIdKey: string,
  prePin: GlobalRef | null,
): Record<string, GlobalRef> =>
  prePin ? { ...globals, [clientIdKey]: { ...prePin } } : globals;

/**
 * The `globals` + stash for a draft moving to `scope`.
 *
 * Re-pinning an already-pinned draft keeps the original stash rather than
 * stashing the value the pin itself forced.
 */
const applyClientIdPin = (
  d: Draft,
  scope: Draft["clientScope"],
  clientIdKey: string,
  globalLib: readonly GlobalAttribute[],
): Pick<Draft, "globals" | "clientIdPrePin" | "referenceRequirementPreForce"> => {
  if (scope !== "specific") {
    const hierarchy = reconcileReferenceHierarchyRequirements(
      releaseClientIdPin(d.globals, clientIdKey, d.clientIdPrePin),
      d.referenceRequirementPreForce,
      globalLib,
    );
    return {
      ...hierarchy,
      clientIdPrePin: null,
    };
  }
  return {
    globals: pinClientId(d.globals, clientIdKey, globalLib),
    referenceRequirementPreForce: d.referenceRequirementPreForce,
    clientIdPrePin:
      d.clientScope === "specific"
        ? d.clientIdPrePin
        : clientIdInCatalog(globalLib, clientIdKey)
          ? (d.globals[clientIdKey] ?? null)
          : null,
  };
};

/**
 * What to call the client attribute when the catalog cannot name it.
 *
 * `clientIdKeyFor` falls back to its first alias when no catalog row matches, so
 * naming the attribute off that key renders the machine slug (`clientId`) in a
 * sentence meant for a person — the exact thing `clientIdRequiredErrorMessage`
 * exists to avoid, and reachable in the one case where the pin note is suppressed
 * (no catalog row) and the server refusal is the user's only signal. The catalog
 * is missing, not the concept, so the message describes the attribute instead of
 * naming it.
 */
const CLIENT_ID_GENERIC_NAME = "the client attribute";

/**
 * Why the client attribute's Requirement and Remove controls are locked.
 *
 * Takes the label from the catalog rather than saying "Client ID": the seeded
 * `client_id` attribute is labelled "Client" once DVB's dedupe migration has
 * run, so a hardcoded name would point at a row the grid does not show. Same
 * reason the removal banner below reads its name off `globalLib`.
 */
const clientIdPinNote = (attributeName: string) =>
  `A space scoped to one or more clients must require ${attributeName} — it is what attributes a document to a client. Switch Client scope to “All clients” to change it.`;

/**
 * The same note plus what the pin is about to CHANGE, for a space whose client
 * attribute was not already assigned-and-Required (DV-684).
 *
 * DV-683 enforces on writes and backfills nothing, so a space saved before it
 * landed opens here with the pin upgrading it on mount — a schema change the user
 * did not ask for and, with only the base note on screen, one nothing announces.
 * `quarantined` means a required attribute is missing (`DocumentLifecycle`), so
 * saying "from now on" is the honest framing: it is certainly true of new uploads,
 * whereas whether DVB re-evaluates already-filed documents is its call, not this
 * editor's, and not something to assert here.
 */
const clientIdPinUpgradeNote = (attributeName: string) =>
  `${clientIdPinNote(attributeName)} This space did not require it before — saving applies the requirement from now on, so a document filed here without a ${attributeName} value will need filing before it counts as filed.`;

function buildDraft(
  source: Space | null,
  globalLib: GlobalAttribute[],
  firstDomain: string,
): Draft {
  const globals: Record<string, GlobalRef> = {};
  globalLib.forEach((a) => {
    globals[a.key] = source?.globals[a.key]
      ? { ...source.globals[a.key] }
      : { on: false, required: false, default: "" };
  });
  // Client ID is on and Required by default for a new space — DV-772. It is a
  // default, not a lock: the Requirement cell stays editable, so a user who
  // wants Optional can still switch it back. Resolve the key from the catalog:
  // writing a hardcoded `clientId` into an environment whose attribute is named
  // `client_id` created a phantom entry and left the real attribute switched off.
  // Guard `required` on `clientIdInCatalog` for the same reason: with no catalog
  // row, `clientIdKeyFor`'s fallback key names an entry the grid never renders,
  // and marking that phantom entry Required would ship a requirement for an
  // attribute that does not exist.
  const clientIdKey = clientIdKeyFor(globalLib);
  if (!globals[clientIdKey]?.on && !source)
    globals[clientIdKey] = {
      on: true,
      required: clientIdInCatalog(globalLib, clientIdKey),
      default: "",
    };
  // A space saved before the Client → Entity → LP rule can open here invalid —
  // an Optional parent, or a child with no parent at all. Deliberately NOT
  // normalized on open: rewriting a saved schema because someone viewed it makes
  // the fix depend on who opens what, and leaves every untouched space invalid.
  // This editor does not migrate those rows. They stay as saved until a follow-up
  // data job; the rule is enforced only on edits made in this session.
  const clientScope = clientScopeFor(source);
  return {
    name: source?.name ?? "",
    domain: source?.domain ?? firstDomain,
    clientScope,
    clients: source?.clients ? [...source.clients] : [],
    clientGlobalIds: [],
    convertToAllClients: false,
    roleScope: source?.roles && source.roles.length ? "specific" : "all",
    roles: source?.roles ? [...source.roles] : [],
    contract: source?.contract ? { ...source.contract } : { ...DEFAULT_CONTRACT },
    globals:
      clientScope === "specific" ? pinClientId(globals, clientIdKey, globalLib) : globals,
    referenceRequirementPreForce: {},
    // Read before the pin overwrites it, so releasing can restore what the space
    // was actually saved with (a legacy scoped space may have had it Optional).
    clientIdPrePin:
      clientScope === "specific" && clientIdInCatalog(globalLib, clientIdKey)
        ? (globals[clientIdKey] ?? null)
        : null,
    locals: source ? source.locals.map((l) => ({ ...l, options: [...(l.options ?? [])] })) : [],
  };
}

/* ---------------- Local attribute add/edit modal ---------------- */
function LocalModal({
  mode,
  initial,
  globalLib,
  siblings,
  savedOptions,
  onCommit,
  onClose,
}: {
  /**
   * Controls edit-mode guardrails. "edit" = persisted attribute; data type and
   * pre-existing options are locked, but the display name is still editable
   * and new options can still be added — the edit is staged onto the draft
   * and persisted by the whole-space save. "add" = new or session-draft
   * attribute; form is fully open.
   * Decoupled from `initial` so that draft locals can pre-fill the form without
   * triggering the locks that only make sense for backend-persisted attributes.
   */
  mode: "add" | "edit";
  initial: SpaceLocalAttribute | null;
  globalLib: GlobalAttribute[];
  siblings: SpaceLocalAttribute[];
  /**
   * Option values this attribute actually had on the backend when the
   * SpaceEditor mounted (empty in "add" mode). `initial` is the draft, which
   * also carries anything staged during the current unsaved space edit —
   * locking off `initial.options` would freeze those too, for as long as the
   * space stays unsaved and however many times this modal is reopened.
   */
  savedOptions: string[];
  onCommit: (attr: SpaceLocalAttribute) => void;
  onClose: () => void;
}) {
  const isEdit = mode === "edit";
  const [name, setName] = useState(initial?.name ?? "");
  const [dataType, setDataType] = useState<AttrDataType>(initial?.dataType ?? "Text");
  const [required, setRequired] = useState(!!initial?.required);
  const [options, setOptions] = useState<string[]>(
    initial?.options ? [...initial.options] : [],
  );
  const [optVal, setOptVal] = useState("");
  /* Minted once per modal, not per render: the slug shown while typing must be the
     exact one submitted, and it is immutable after creation. */
  const [suffix] = useState(newAttributeSlugSuffix);
  // On edit the slug is fixed to the persisted value (slug, or the id for
  // pre-slug locals); on create it's minted from the display name + suffix.
  const storedSlug = initial?.slug ?? initial?.id;
  const slug = isEdit && storedSlug ? storedSlug : mintAttributeSlug(name, suffix);
  const selectType = isSelectType(dataType);
  // Two attributes with one name are indistinguishable on the upload form and in
  // the grid, so a clash blocks the commit rather than just warning.
  const { conflict: nameConflict, isChecking: isCheckingName } = useAttributeNameConflict({
    name,
    // On edit the slug is frozen, so renaming can introduce a name clash but never
    // a new slug clash.
    slug: isEdit ? undefined : slug,
    globalAttributes: globalLib,
    localAttributes: siblings,
    ignoreLocalId: initial?.id,
  });
  const ok =
    name.trim() !== "" &&
    !nameConflict &&
    !isCheckingName &&
    (!selectType || options.length > 0);
  // DV-468: dropping a repeat silently looked like Add was broken.
  const optIsDuplicate = isDuplicateAttributeValue(optVal, options);

  const addOption = () => {
    const t = optVal.trim();
    if (!t) {
      setOptVal("");
      return;
    }
    if (optIsDuplicate) return;
    setOptions([...options, t]);
    setOptVal("");
  };
  const commit = () => {
    if (!ok) return;
    onCommit({
      id: initial?.id ?? `l_${slugify(name)}_${name.length}`,
      // Slug is minted once (create) and preserved verbatim on edit/rename.
      slug: initial?.slug ?? slug,
      name: name.trim(),
      dataType,
      required,
      // Defaults are not persisted for space-locals (no backend field); only
      // select picklists carry a value set via ``options``.
      default: "",
      options: selectType ? options : [],
    });
  };

  return (
    <ModalV2
      show
      onHide={onClose}
      size="medium"
      closeOnClickOutside={false}
      title={isEdit ? "Edit space-local attribute" : "New space-local attribute"}
    >
      <ModalV2.Body>
        <div className={styles.field}>
          <TextInput
            label="Display name"
            value={name}
            placeholder="e.g. Source System"
            isFullWidth
            onChange={(e) => setName(e.target.value)}
          />
          {nameConflict && (
            <div className={styles.fieldError} role="alert">
              {attributeNameConflictMessage(nameConflict)}
            </div>
          )}
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            Attribute slug {isEdit ? "· read-only" : "· generated from the display name"}
          </span>
          <input
            className={styles.slugInput}
            value={slug}
            placeholder="set_on_creation"
            readOnly
            disabled
          />
          <div className={styles.help}>
            {isEdit
              ? "The slug is fixed once created — reporting and integrations reference it, so it never changes even when you rename the attribute."
              : "Minted from the display name now and stays fixed afterward. Rename freely later; the slug won't change. A short random suffix keeps it unique across the workspace."}
          </div>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Data type {isEdit && <ReadOnlyChip />}</span>
          <Select
            value={dataType}
            full
            disabled={isEdit}
            onChange={(v) => {
              setDataType(v as AttrDataType);
              setOptions([]);
            }}
          >
            <Select.Section>
              {EDITABLE_DATATYPES.map((t) => (
                <Select.Option key={t} value={t}>
                  {t}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Requirement</span>
          <div className={styles.reqSeg}>
            <RadioBar
              value={required ? "required" : "optional"}
              onSelectOption={(v) => setRequired(v === "required")}
              options={[
                { label: "Required", value: "required" },
                { label: "Optional", value: "optional" },
              ]}
            />
          </div>
        </div>
        {selectType && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Allowed options</span>
            <div className={styles.optChips}>
              {options.length === 0 && <span className={styles.latMuted}>No options yet</span>}
              {options.map((o) => {
                const saved = savedOptions.includes(o);
                return (
                  <span className={styles.optChip} key={o}>
                    {o}
                    <button
                      className={styles.optChipX}
                      onClick={() => setOptions(options.filter((x) => x !== o))}
                      aria-label={`Remove ${o}`}
                      type="button"
                      disabled={saved}
                    >
                      <Icon type="xmark" size="small" />
                    </button>
                  </span>
                );
              })}
            </div>
            {options.some((o) => savedOptions.includes(o)) && (
              <div className={styles.help}>{SAVED_VALUE_SET_LOCK_MESSAGE}</div>
            )}
            <div className={styles.optAdd}>
              <div className={styles.optAddInput}>
                <TextInput
                  value={optVal}
                  placeholder="Add an option…"
                  onChange={(e) => setOptVal(e.target.value)}
                />
              </div>
              <Button variant="neutral" disabled={optIsDuplicate} onClick={addOption}>
                Add
              </Button>
            </div>
            {optIsDuplicate && (
              <div className={styles.fieldError} role="alert">
                {DUPLICATE_VALUE_MESSAGE}
              </div>
            )}
          </div>
        )}
      </ModalV2.Body>
      <ModalV2.Footer>
        <div className={styles.modalFooter}>
          <Button variant="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!ok} onClick={commit}>
            {isEdit ? "Save changes" : "Add attribute"}
          </Button>
        </div>
      </ModalV2.Footer>
    </ModalV2>
  );
}

/* ---------------- Live preview ---------------- */
function LivePreview({ draft, globalLib }: { draft: Draft; globalLib: GlobalAttribute[] }) {
  const fields = [
    ...globalLib
      .filter((a) => draft.globals[a.key]?.on)
      .map((a) => ({
        scope: "global" as const,
        name: a.name,
        def: draft.globals[a.key].default,
        values: a.values,
      })),
    ...draft.locals.map((l) => ({
      scope: "local" as const,
      name: l.name || "Untitled attribute",
      def: l.default ?? "",
      values: l.options ?? null,
    })),
  ];
  return (
    <div className={styles.preview}>
      <div className={styles.previewHead}>
        <span className={styles.previewEyebrow}>
          <Icon type="eye" size="small" /> Preview
        </span>
        <div className={styles.previewTitle}>What lands in this space will carry</div>
      </div>
      <div className={styles.previewForm}>
        <div className={styles.pvRow}>
          <span className={styles.pvLabel}>Space</span>
          <div className={styles.pvControl}>{draft.name || "Untitled space"}</div>
        </div>
        {fields.length === 0 ? (
          <div className={styles.pvEmpty}>
            Reference a global attribute or add a space-local one to see the form build up.
          </div>
        ) : (
          fields.map((f, i) => (
            <div className={styles.pvRow} key={i}>
              <span className={styles.pvLabel}>
                {f.name}
                <span
                  className={f.scope === "global" ? styles.pvTier.global : styles.pvTier.local}
                >
                  {f.scope === "global" ? "Global" : "Local"}
                </span>
              </span>
              <div className={styles.pvControl}>
                <span>
                  {f.def || (f.values && f.values.length ? f.values.join(" · ") : "Select…")}
                </span>
                <Icon type="chevron-down" size="small" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ---------------- Referenced-globals grid (AG Grid) ---------------- */
type GlobalGridRow = {
  key: string;
  name: string;
  dataType: string;
  captures: string;
  required: boolean;
  default: string;
  values: string[] | null;
  validates: string;
  /**
   * The assignment is fixed by the space's client scope (DV-684) — Requirement
   * is not editable and the row cannot be removed.
   *
   * NOT `pinned`: the `remove` colDef below sets AG Grid's own `pinned: "right"`
   * in the same `cols` array, and one word meaning two unrelated things there is
   * a trap. Matches `styles.lockedCell`.
   */
  assignmentLocked: boolean;
  requirementLockedReason: string | null;
  removeLockedReason: string | null;
};

function GlobalRequirementCell({ data }: { data?: GlobalGridRow }) {
  if (!data) return null;
  const label = data.required ? "Required" : "Optional";
  if (!data.requirementLockedReason) return <>{label}</>;
  // No `title` on any locked cell: both this column and `remove` carry a
  // `tooltipValueGetter` with the same sentence, and the grid does not set
  // `enableBrowserTooltips` — so AG Grid would render its own tooltip AND the
  // browser the native one after its own delay, staggering the same text twice.
  return (
    <span className={styles.lockedCell}>
      <Icon type="lock" size="small" />
      {label}
    </span>
  );
}

function GlobalRemoveCell({ data }: { data?: GlobalGridRow }) {
  if (!data) return null;
  if (data.assignmentLocked)
    return (
      <span className={styles.lockedCell} aria-label={`${data.name} can’t be removed`}>
        <Icon type="lock" size="small" />
      </span>
    );
  if (data.removeLockedReason)
    return (
      <span
        className={styles.disabledIconBtn}
        aria-label={data.removeLockedReason}
        aria-disabled="true"
      >
        <Icon type="xmark" size="small" />
      </span>
    );
  return (
    <span className={styles.iconBtn} aria-label={`Remove ${data.name}`}>
      <Icon type="xmark" size="small" />
    </span>
  );
}

function SpaceGlobalsGrid({
  rows,
  onSetRequired,
  onSetDefault,
  onRemove,
}: {
  rows: GlobalGridRow[];
  onSetRequired: (key: string, required: boolean) => void;
  onSetDefault: (key: string, def: string) => void;
  onRemove: (key: string) => void;
}) {
  const cols: AgGrid.ColDef<GlobalGridRow>[] = [
    { headerName: "Attribute", field: "name", flex: 1.4, minWidth: 160 },
    { headerName: "Data type", field: "dataType", width: 120, cellStyle: { color: "#72716e" } },
    {
      headerName: "Captures",
      field: "captures",
      flex: 1.8,
      minWidth: 190,
      cellStyle: { color: "#72716e" },
      tooltipField: "captures",
    },
    {
      headerName: "Requirement",
      colId: "req",
      width: 150,
      editable: (p) => !p.data?.requirementLockedReason,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: { values: ["Required", "Optional"] },
      cellRenderer: GlobalRequirementCell,
      tooltipValueGetter: (p) => p.data?.requirementLockedReason,
      valueGetter: (p) => (p.data?.required ? "Required" : "Optional"),
      valueSetter: (p) => {
        if (p.data.requirementLockedReason) return false;
        onSetRequired(p.data.key, p.newValue === "Required");
        return false;
      },
    },
    {
      headerName: "Default",
      colId: "def",
      flex: 1,
      minWidth: 150,
      editable: (p) => !!p.data?.values,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: (p: { data?: GlobalGridRow }) => ({
        values: ["No default", ...(p.data?.values ?? [])],
      }),
      valueGetter: (p) =>
        p.data?.values ? p.data.default || "No default" : p.data?.validates || "",
      valueSetter: (p) => {
        onSetDefault(p.data.key, p.newValue === "No default" ? "" : String(p.newValue ?? ""));
        return false;
      },
      cellStyle: (p: AgGrid.CellClassParams<GlobalGridRow>) =>
        p.data?.values ? null : { color: "#00614e" },
    },
    {
      headerName: "",
      colId: "remove",
      width: 56,
      sortable: false,
      filter: false,
      resizable: false,
      pinned: "right",
      lockPinned: true,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      cellRenderer: GlobalRemoveCell,
      tooltipValueGetter: (p) =>
        p.data?.assignmentLocked
          ? clientIdPinNote(p.data.name)
          : (p.data?.removeLockedReason ?? null),
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
  ];
  return (
    <div className={styles.spaceGridWrap}>
      <Grid<GlobalGridRow>
        rowData={rows}
        columnDefs={cols}
        defaultColDef={{
          sortable: true,
          resizable: true,
          filter: true,
          menuTabs: ["filterMenuTab", "generalMenuTab"],
        }}
        getRowId={(p) => p.data.key}
        // AG Grid defaults to 2s, long enough that a locked control reads as
        // unexplained before its reason appears.
        tooltipShowDelay={300}
        rowHeight={46}
        headerHeight={42}
        domLayout="autoHeight"
        singleClickEdit
        stopEditingWhenCellsLoseFocus
        sideBar={{
          toolPanels: [
            {
              id: "columns",
              labelDefault: "Columns",
              labelKey: "columns",
              iconKey: "columns",
              toolPanel: "agColumnsToolPanel",
            },
            {
              id: "filters",
              labelDefault: "Filters",
              labelKey: "filters",
              iconKey: "filter",
              toolPanel: "agFiltersToolPanel",
            },
          ],
        }}
        onCellClicked={(e) => {
          if (
            e.column.getColId() === "remove" &&
            e.data &&
            !e.data.assignmentLocked &&
            !e.data.removeLockedReason
          )
            onRemove(e.data.key);
        }}
      />
    </div>
  );
}

/* ---------------- Component ---------------- */
export function SpaceEditor({
  source,
  domains,
  globalLib,
  onDeletionImpact,
  allSpaces: _allSpaces,
  reference,
  isSteward = false,
  showPreview = true,
  onAddGlobalAttr: _onAddGlobalAttr,
  saving = false,
  onSave,
  onCancel,
  clientSearch = false,
}: SpaceEditorProps) {
  const [draft, setDraft] = useState<Draft>(() =>
    buildDraft(source, globalLib, domains[0]?.id ?? ""),
  );
  /** Set when the server refused this name as a duplicate within the domain. */
  const [nameError, setNameError] = useState<string | null>(null);
  /**
   * Set when the server refused the space's client-attribute assignment
   * (DV-683). Rendered against the Global attributes panel, since it names an
   * assignment the user can go and fix; every other save failure is the
   * route's toast.
   */
  const [attributesError, setAttributesError] = useState<string | null>(null);

  // The Client ID attribute as this catalog names it — drives the default-on
  // behaviour, the client-scope pin, the "Default" lock in the picker, and the
  // removal confirmation. `clientIdName` is its display label, which DVB's
  // dedupe migration relabels to "Client", so it is never assumed — and falls
  // back to a phrase rather than the key, so no message ever shows a slug.
  const clientIdKey = useMemo(() => clientIdKeyFor(globalLib), [globalLib]);
  const clientIdName = useMemo(
    () => globalLib.find((g) => g.key === clientIdKey)?.name ?? CLIENT_ID_GENERIC_NAME,
    [globalLib, clientIdKey],
  );
  const hierarchyError = referenceHierarchyError(draft.globals, globalLib);

  /**
   * Per-client scoping at CREATE time ("One or more clients" + the Clients
   * picker beneath it). Unlike Sharing and the Space-local tab, the code
   * behind this one is fully backed — it was off purely as a DV-501 scope
   * decision. Resolved directly rather than threaded in as a prop — same
   * pattern as `useFeatureFlag("showDocumentVault", ...)` in
   * `fund-admin-frontend` and `useFeatureFlag("internationalPaymentRails", ...)`
   * in `jsq-payments-components`'s `BaseWorkflow`.
   */
  const clientScopingEnabled = useFeatureFlag("documentVaultM2", false);

  /**
   * The space is SAVED as one-or-more-clients, so moving it to all-clients is
   * the irreversible backend transition (DV-500) rather than a field edit.
   *
   * `appliesToAllClients === false` is checked explicitly: an all-clients space
   * has nothing to add to and nothing left to convert, and a space whose flag is
   * unknown (drafts, fixtures) must not be guessed at from `clients`.
   *
   * Deliberately independent of `isSteward`: stewardship governs who sees the
   * management panel, not what the transition costs. Gating the confirmation on
   * it let a non-steward flip the radio in one unconfirmed click — releasing the
   * Client ID pin and clearing the staged clients — while `convertToAllClients`
   * stayed false, so the space was still client-scoped and the save was refused
   * server-side. That is the affordance-whose-only-outcome-is-a-server-error
   * this pin set out to remove.
   */
  const isSavedClientScoped = source !== null && source.appliesToAllClients === false;

  /**
   * DV-501 client-scope management panel. Deliberately NOT gated on
   * `clientScopingEnabled`, which governs choosing a scope at CREATE time
   * (out of scope for this ticket) — this manages the scope of a space that is
   * already saved as one-or-more-clients, and stewardship is what gates seeing it.
   */
  const showClientScopePanel = isSavedClientScoped && isSteward;

  /**
   * A space that already applies to all clients can never be re-scoped —
   * `appliesToAllClients: true` is a one-way door on the backend (DV-500), which
   * refuses the reverse transition. Offering "One or more clients" here would be
   * an affordance whose only outcome is a server error, so it is locked.
   */
  const clientScopeLocked = source?.appliesToAllClients === true;

  /**
   * Open while the Client scope radio is asking the user to confirm converting
   * a saved, client-scoped space to all-clients. The radio is the ONLY way to
   * start that transition — the panel below deliberately offers no second
   * control for the same thing.
   */
  const [convertPromptOpen, setConvertPromptOpen] = useState(false);

  /**
   * Client scope changed on the radio.
   *
   * Moving a SAVED scoped space to "All clients" is the irreversible one, so it
   * is routed through the confirmation instead of applying on the click; every
   * other move (including undoing a staged conversion, and anything on the
   * create path) is a plain field edit.
   */
  const selectClientScope = (next: Draft["clientScope"]) => {
    if (next === "firm" && isSavedClientScoped && !draft.convertToAllClients) {
      setConvertPromptOpen(true);
      return;
    }
    setAttributesError(null);
    // A Client ID removal confirmation opened while firm-wide would otherwise
    // survive the switch to specific and strip the attribute the pin just
    // locked — `confirmRemove` writes `{ on: false }` straight through.
    setPendingRemove((p) =>
      next === "specific" && p?.scope === "global" && p.key === clientIdKey ? null : p,
    );
    setDraft((d) => ({
      ...d,
      clientScope: next,
      clients:
        next === "firm"
          ? []
          : // Undoing a staged conversion: `confirmConvert` cleared `clients` to
            // stage the all-clients payload, so bouncing back to "specific" has
            // to restore the saved set rather than leave the draft at zero
            // clients — with `clientScopeOk` now requiring at least one for a
            // scoped space, an empty draft here would strand every other field
            // edit until an unrelated client was added.
            d.convertToAllClients
            ? (source?.clients ?? [])
            : d.clients,
      clientGlobalIds: next === "firm" ? [] : d.clientGlobalIds,
      // Going back to specific clients un-stages a conversion that hasn't been
      // saved yet.
      convertToAllClients: next === "firm" ? d.convertToAllClients : false,
      // Going firm-wide restores the assignment the pin overwrote; going specific
      // pins it and stashes what it replaced.
      ...applyClientIdPin(d, next, clientIdKey, globalLib),
    }));
  };

  const confirmConvert = () => {
    // Same reason as `selectClientScope`: confirming the conversion resolves a
    // CLIENT_ID_REQUIRED refusal, so the alert must not outlive it.
    setAttributesError(null);
    // Staging the conversion drops any staged additions: the backend refuses
    // `addClientIds` alongside `appliesToAllClients: true`, and the rows are
    // cleared anyway.
    setDraft((d) => ({
      ...d,
      clientScope: "firm",
      convertToAllClients: true,
      clients: [],
      clientGlobalIds: [],
      // This path sets the scope itself rather than going through
      // `selectClientScope`, so it has to release the pin on its own.
      ...applyClientIdPin(d, "firm", clientIdKey, globalLib),
    }));
    setConvertPromptOpen(false);
  };
  const [addOpen, setAddOpen] = useState(false);
  const [editingLocal, setEditingLocal] = useState<{
    mode: "add" | "edit";
    attr: SpaceLocalAttribute | null;
  } | null>(null);

  /**
   * Ids of locals that were already persisted to the backend when the editor
   * opened. Mount-only snapshot: SpaceEditor is expected to unmount on save, so
   * this ref never becomes stale. Used to gate edit-mode guardrails: only truly
   * persisted locals get the data-type lock and option-removal restrictions;
   * session-added drafts are treated as brand-new (mode "add").
   */
  const persistedLocalIds = useRef<Set<string>>(
    new Set((source?.locals ?? []).map((l) => l.id)),
  );

  /**
   * Option values each local attribute actually had on the backend when the
   * editor mounted. Also a mount-only snapshot, for the same reason as
   * `persistedLocalIds`: `draft.locals` picks up whatever gets staged during
   * this edit (e.g. a new option added and "saved" onto the draft without a
   * mutation running), and locking removal off that would freeze anything
   * staged this session for as long as the space stays unsaved.
   */
  const persistedLocalOptions = useRef<Map<string, string[]>>(
    new Map((source?.locals ?? []).map((l) => [l.id, [...(l.options ?? [])]])),
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // The convert confirmation is stacked on top of this takeover. Escape
      // belongs to the innermost thing that is open, so it dismisses the
      // confirmation and leaves the editor — and the user's unsaved edits —
      // alone. Closing both at once discarded the draft to answer a question
      // about one field.
      if (convertPromptOpen) {
        setConvertPromptOpen(false);
        return;
      }
      onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, convertPromptOpen]);

  const reconcileDraftGlobals = (d: Draft, globals: Record<string, GlobalRef>) => {
    const hierarchy = reconcileReferenceHierarchyRequirements(
      globals,
      d.referenceRequirementPreForce,
      globalLib,
    );
    return {
      ...hierarchy,
      globals:
        d.clientScope === "specific"
          ? pinClientId(hierarchy.globals, clientIdKey, globalLib)
          : hierarchy.globals,
    };
  };

  const setGlobal = (key: string, patch: Partial<GlobalRef>) => {
    setAttributesError(null);
    setDraft((d) => {
      const globals = { ...d.globals, [key]: { ...d.globals[key], ...patch } };
      return { ...d, ...reconcileDraftGlobals(d, globals) };
    });
  };

  const hasReferencedDescendants = (key: string) =>
    referenceDescendantKeys(key, globalLib).some(
      (descendantKey) => draft.globals[descendantKey]?.on,
    );

  const toggleGlobalFromPicker = (key: string) => {
    const turningOff = draft.globals[key]?.on;
    // Same child-first rule as the grid X: a parent with live descendants is a
    // no-op. Cascading here would drop those children from a saved space without
    // the confirmation the grid requires (`requestRemoveGlobal`).
    if (turningOff && hasReferencedDescendants(key)) return;
    setAttributesError(null);
    setDraft((d) => {
      const on = !d.globals[key]?.on;
      const globals = {
        ...d.globals,
        [key]: { ...d.globals[key], on },
      };
      return { ...d, ...reconcileDraftGlobals(d, globals) };
    });
  };

  /**
   * Client ID is pinned while the draft is scoped to specific clients: DV-683
   * refuses that write without it, so removing it or downgrading it to Optional
   * are affordances whose only outcome is a server error.
   */
  const clientIdPinned =
    draft.clientScope === "specific" &&
    // Nothing to pin, lock, or explain when the catalog carries no Client ID
    // attribute and `clientIdKey` is `clientIdKeyFor`'s fallback alias: the grid
    // renders no row for it, so the note would name one that does not exist.
    globalLib.some((g) => g.key === clientIdKey);

  /**
   * The pin is upgrading this space rather than restating what it already had —
   * `clientIdPrePin` is the assignment it overwrote (null when nothing is pinned).
   */
  const clientIdPinUpgrades =
    clientIdPinned &&
    !!draft.clientIdPrePin &&
    !(draft.clientIdPrePin.on && draft.clientIdPrePin.required);

  const requirementLockedReasonFor = (attribute: GlobalAttribute): string | null => {
    const reasons: string[] = [];
    if (clientIdPinned && attribute.key === clientIdKey)
      reasons.push(clientIdPinNote(attribute.name));

    const dependentNames = referenceDescendantKeys(attribute.key, globalLib)
      .filter((key) => draft.globals[key]?.on)
      .map((key) => globalLib.find((candidate) => candidate.key === key)?.name ?? key);
    if (dependentNames.length > 0)
      reasons.push(
        `${dependentNames.join(" and ")} ${
          dependentNames.length === 1 ? "depends" : "depend"
        } on ${attribute.name}, so ${attribute.name} must be Required.`,
      );

    return reasons.length > 0 ? reasons.join(" ") : null;
  };

  const removeLockedReasonFor = (attribute: GlobalAttribute): string | null => {
    const dependentNames = referenceDescendantKeys(attribute.key, globalLib)
      .filter((key) => draft.globals[key]?.on)
      .map((key) => globalLib.find((candidate) => candidate.key === key)?.name ?? key);
    if (dependentNames.length === 0) return null;
    return `Remove ${dependentNames.join(" and ")} before removing ${attribute.name}.`;
  };

  const refGlobals = useMemo(
    () =>
      sortGlobalsByRolePriority(
        globalLib.filter((a) => draft.globals[a.key]?.on),
        globalLib,
      ),
    [globalLib, draft.globals],
  );

  const commitLocal = (attr: SpaceLocalAttribute) => {
    setDraft((d) => {
      const exists = d.locals.some((l) => l.id === attr.id);
      return {
        ...d,
        locals: exists
          ? d.locals.map((l) => (l.id === attr.id ? attr : l))
          : [...d.locals, attr],
      };
    });
    setEditingLocal(null);
  };
  const removeLocal = (id: string) =>
    setDraft((d) => ({ ...d, locals: d.locals.filter((l) => l.id !== id) }));

  // Attribute removal is confirmed first: Client ID shows an inline warning
  // banner (access-control implications); any other global/local shows a
  // forward-looking removal modal with the affected document count.
  const [pendingRemove, setPendingRemove] = useState<{
    scope: "global" | "local";
    key: string;
    name: string;
  } | null>(null);

  // For a NEW space there are no filed documents yet, so the forward-looking
  // removal modal is skipped — attributes remove immediately. Client ID always
  // confirms (access-control implications), and editing an existing space keeps
  // the confirmation for every attribute.
  const isNewSpace = !source;

  /**
   * How many documents carry a value for the space-local attribute being removed.
   * `idle` renders nothing — the state a global removal stays in, since the
   * backing count is per-attribute rather than per-space (see `onDeletionImpact`).
   */
  const [removeImpact, setRemoveImpact] = useState<
    { status: "idle" | "loading" | "unavailable" } | { status: "ready"; count: number }
  >({ status: "idle" });
  const removeImpactRequestId = useRef(0);

  const loadRemoveImpact = async (attributeId: string) => {
    const requestId = ++removeImpactRequestId.current;
    if (!onDeletionImpact) {
      if (requestId === removeImpactRequestId.current) {
        setRemoveImpact({ status: "unavailable" });
      }
      return;
    }
    setRemoveImpact({ status: "loading" });
    try {
      const count = await onDeletionImpact(attributeId);
      if (requestId !== removeImpactRequestId.current) return;
      setRemoveImpact(count === null ? { status: "unavailable" } : { status: "ready", count });
    } catch {
      if (requestId !== removeImpactRequestId.current) return;
      setRemoveImpact({ status: "unavailable" });
    }
  };

  const closeRemoveConfirmation = () => {
    removeImpactRequestId.current += 1;
    setPendingRemove(null);
    setRemoveImpact({ status: "idle" });
  };

  const requestRemoveGlobal = (key: string) => {
    // The grid locks this row's control, so reaching here means a stale click.
    if (key === clientIdKey && clientIdPinned) return;
    if (hasReferencedDescendants(key)) return;
    if (isNewSpace && key !== clientIdKey) {
      setGlobal(key, { on: false });
      return;
    }
    setRemoveImpact({ status: "idle" });
    setPendingRemove({
      scope: "global",
      key,
      name: globalLib.find((g) => g.key === key)?.name ?? key,
    });
  };
  const requestRemoveLocal = (id: string, name: string) => {
    if (isNewSpace) {
      removeLocal(id);
      return;
    }
    setPendingRemove({ scope: "local", key: id, name });
    if (persistedLocalIds.current.has(id)) void loadRemoveImpact(id);
  };
  const confirmRemove = () => {
    if (!pendingRemove) return;
    // `selectClientScope` dismisses this banner when the pin engages, so
    // reaching here pinned means a stale click. Removing anyway left the grid
    // row gone while the note still called it required, `canSave` true, and the
    // save going out as a client-scoped space with no client attribute — the
    // exact state DV-683 refuses.
    if (
      pendingRemove.scope === "global" &&
      pendingRemove.key === clientIdKey &&
      clientIdPinned
    ) {
      closeRemoveConfirmation();
      return;
    }
    if (pendingRemove.scope === "global") {
      if (hasReferencedDescendants(pendingRemove.key)) {
        closeRemoveConfirmation();
        return;
      }
      setGlobal(pendingRemove.key, { on: false });
    } else removeLocal(pendingRemove.key);
    closeRemoveConfirmation();
  };
  const isClientIdRemoval =
    pendingRemove?.scope === "global" && pendingRemove.key === clientIdKey;

  // On the create path `draft.clients` IS the client set. On the edit path it is
  // seeded from the saved set (the read-only `Select` fallback below renders it)
  // and then replaced wholesale by the picker — so it is a mix, not a pure list
  // of additions. The space's saved clients come from `source` and are never
  // removed here.
  const savedClients = source?.clients ?? [];

  // Staged client additions on the edit path: the part of the draft selection the
  // space doesn't already have. Filtering matters on mount, where the seeded
  // draft is exactly the saved set and an unfiltered count would treat every
  // existing client as a pending addition.
  const stagedClientIds = draft.clients.filter((id) => !savedClients.includes(id));

  // What the space actually has if saved right now. NOT `draft.clients` alone:
  // on the edit path `ClientScopePanel` seeds and replaces `draft.clients`
  // with only the staged *additions* (see `stagedClientIds` above), so
  // un-staging one lands it at `[]` even though `savedClients` still holds
  // the real set. Derived once and reused below and in `save()` so the two
  // can't read a different list.
  const effectiveClients = [...new Set([...savedClients, ...draft.clients])];

  /**
   * "Specific clients" needs at least one client — a scoped space with no
   * clients is not a state the backend allows, on create or edit alike.
   */
  const clientScopeOk = draft.clientScope === "firm" || effectiveClients.length > 0;
  const roleScopeOk = draft.roleScope === "all" || draft.roles.length > 0;
  const canSave =
    draft.name.trim() !== "" && clientScopeOk && roleScopeOk && hierarchyError === null;

  const save = async () => {
    if (!canSave) return;
    const stagedConvert = draft.convertToAllClients;
    const space: Space = {
      id: source?.id ?? `sp_new_${slugify(draft.name)}`,
      name: draft.name.trim(),
      domain: draft.domain,
      contract: draft.contract,
      globals: draft.globals,
      locals: draft.locals,
      // A staged conversion clears the list; otherwise the effective set. The
      // mapper sends this list verbatim as a full-state replace, so it has to
      // be the union up front — there is no diffing downstream to reconstruct
      // it from an addition.
      clients:
        stagedConvert || (!source && draft.clientScope === "firm")
          ? undefined
          : effectiveClients,
      // Carried from the source unless a conversion is staged. NOT re-derived
      // from `clients`: `appliesToAllClients: true` is a one-way door on the
      // backend, and the mapper needs to tell "already firm-wide" (leave alone)
      // from "convert now" (send the flag) — see `vaultSpaceToUpdateInput`.
      appliesToAllClients: stagedConvert ? true : source?.appliesToAllClients,
      roles: draft.roleScope === "specific" ? draft.roles : undefined,
    };
    setNameError(null);
    setAttributesError(null);
    try {
      await onSave(space, source?.id ?? null);
    } catch (error) {
      // This takeover owns only the error shapes it can point at something on
      // screen; any other rejection is left to the caller (already logged and
      // toasted there).
      const clientIdRefusal = clientIdRequiredErrorMessage(clientIdName, error);
      if (isDuplicateSpaceNameError(error)) {
        setNameError("A space with this name already exists. Space names must be unique.");
      } else if (clientIdRefusal) {
        setAttributesError(clientIdRefusal);
      }
    }
  };

  return (
    <div
      className={styles.takeover}
      role="dialog"
      aria-modal="true"
      aria-label={source ? "Edit space" : "New space"}
    >
      <div className={styles.bar}>
        <div className={styles.exit}>
          <Button variant="muted" icon="xmark" iconPosition="left" onClick={onCancel}>
            Exit
          </Button>
        </div>
        <h2 className={styles.barTitle}>{source ? "Edit space" : "New space"}</h2>
      </div>

      <div className={styles.scroll}>
        <div className={styles.content}>
          <div className={showPreview ? styles.grid.withPreview : styles.grid.full}>
            <div className={styles.authoring}>
              <div className={styles.editorDetails}>
                {/* Space details */}
                <div className={styles.panel}>
                  <div className={styles.panelHead}>
                    <h3 className={styles.panelHeadTitle}>Space details</h3>
                  </div>
                  <div className={styles.panelBody}>
                    <div className={styles.field}>
                      <TextInput
                        label="Space name"
                        value={draft.name}
                        placeholder="e.g. Financial Statements — DEX"
                        onChange={(e) => {
                          setDraft((d) => ({ ...d, name: e.target.value }));
                          setNameError(null);
                        }}
                        isFullWidth
                      />
                      {nameError && (
                        <div className={styles.fieldError} role="alert">
                          {nameError}
                        </div>
                      )}
                    </div>
                    <div className={styles.field}>
                      <Select
                        label={
                          <span className={styles.domainTagLabel}>
                            Domain tag
                            <HelpIndicator
                              id="space-editor-domain-tag-tooltip"
                              content={DOMAIN_TAG_TOOLTIP}
                              placement="top"
                            >
                              <span
                                className={styles.domainTagHelpIcon}
                                aria-label="About Domain tag"
                              >
                                <Icon type="info-circle" size="small" variant="outline" />
                              </span>
                            </HelpIndicator>
                          </span>
                        }
                        value={draft.domain}
                        full
                        disabled={!!source}
                        onChange={(v) => setDraft((d) => ({ ...d, domain: String(v) }))}
                      >
                        <Select.Section>
                          {domains.map((dm) => (
                            <Select.Option key={dm.id} value={dm.id}>
                              {dm.name}
                            </Select.Option>
                          ))}
                        </Select.Section>
                      </Select>
                    </div>

                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>Client scope</span>
                      <RadioBar
                        value={draft.clientScope}
                        isFullWidth
                        onSelectOption={(v) => selectClientScope(v as Draft["clientScope"])}
                        options={[
                          { label: "All clients", value: "firm" },
                          {
                            label: "One or more clients",
                            value: "specific",
                            // `clientScopingEnabled` governs CHOOSING this
                            // scope, but the radio is also the only way to undo
                            // a staged conversion — so it has to stay reachable
                            // while one is pending, or the user is trapped in a
                            // change they haven't saved yet.
                            isDisabled:
                              (!clientScopingEnabled && !draft.convertToAllClients) ||
                              clientScopeLocked,
                          },
                        ]}
                      />
                      {clientScopeLocked && (
                        <div className={styles.help} data-testid="client-scope-locked">
                          This space applies to all clients. That can’t be reversed, so it can’t
                          be scoped back to specific clients.
                        </div>
                      )}
                      {/* Superseded by the DV-501 Clients panel below, which
                          shows the same set and can actually act on it. */}
                      {draft.clientScope === "specific" && !showClientScopePanel && (
                        <div className={styles.scopeList}>
                          {/* The real, WIS-backed picker. Mounted only on the
                              connected path (`clientSearch`), since it fetches its
                              own arenas; the presentational fallback below keeps
                              stories and component tests renderable. */}
                          {clientScopingEnabled && clientSearch ? (
                            <>
                              <span className={styles.fieldLabel}>Clients</span>
                              <ClientPicker
                                value={{
                                  ids: draft.clients,
                                  globalIds: draft.clientGlobalIds,
                                }}
                                testId="space-create-client-search"
                                onChange={(selection) =>
                                  setDraft((d) => ({
                                    ...d,
                                    clients: selection.ids,
                                    clientGlobalIds: selection.globalIds,
                                  }))
                                }
                              />
                              <ClientScopeHelper />
                            </>
                          ) : (
                            <Select
                              multiple
                              value={draft.clients}
                              placeholder="Select clients"
                              label="Clients"
                              full
                              // Reachable only for a space already scoped to specific
                              // clients (the radio option itself is disabled): the set is
                              // shown, but not editable while scoping is switched off.
                              disabled={!clientScopingEnabled}
                              onChange={(v) =>
                                setDraft((d) => ({ ...d, clients: v as string[] }))
                              }
                            >
                              <Select.Section>
                                {reference.clients.map((c) => (
                                  <Select.Option key={c} value={c}>
                                    {c}
                                  </Select.Option>
                                ))}
                              </Select.Section>
                            </Select>
                          )}
                        </div>
                      )}
                    </div>

                    {showClientScopePanel && (
                      <ClientScopePanel
                        spaceName={source.name}
                        clients={source.clients ?? []}
                        clientNames={source.clientNames}
                        clientSearch={clientSearch}
                        staged={{
                          ids: stagedClientIds,
                          globalIds: draft.clientGlobalIds,
                        }}
                        onStageClients={(selection) =>
                          setDraft((d) => ({
                            ...d,
                            clients: selection.ids,
                            clientGlobalIds: selection.globalIds,
                          }))
                        }
                        convertStaged={draft.convertToAllClients}
                      />
                    )}

                    {convertPromptOpen && source && (
                      <ConvertToAllClientsModal
                        spaceName={source.name}
                        clientCount={source.clients?.length ?? 0}
                        onConfirm={confirmConvert}
                        onClose={() => setConvertPromptOpen(false)}
                      />
                    )}

                    {/* <div className={styles.field}>
                      <span className={styles.fieldLabel}>Canopy role scope</span>
                      <RadioBar
                        value={draft.roleScope}
                        isFullWidth
                        onSelectOption={(v) =>
                          setDraft((d) => ({
                            ...d,
                            roleScope: v as Draft["roleScope"],
                            roles: v === "all" ? [] : d.roles,
                          }))
                        }
                        options={[
                          { label: "Any role", value: "all" },
                          { label: "Restricted roles", value: "specific" },
                        ]}
                      />
                      {draft.roleScope === "specific" && (
                        <div className={styles.scopeList}>
                          <Select
                            multiple
                            value={draft.roles}
                            placeholder="Select roles"
                            label="Roles"
                            full
                            onChange={(v) => setDraft((d) => ({ ...d, roles: v as string[] }))}
                          >
                            <Select.Section>
                              {reference.roles.map((r) => (
                                <Select.Option key={r} value={r}>
                                  {r}
                                </Select.Option>
                              ))}
                            </Select.Section>
                          </Select>
                        </div>
                      )}
                    </div> */}
                  </div>
                </div>
              </div>

              <div className={styles.editorAttrs}>
                {/* Global attributes */}
                <div className={styles.panel}>
                  <div className={styles.panelHead}>
                    <h3 className={styles.panelHeadTitle}>Global attributes</h3>
                    <Button
                      variant="primary"
                      icon="plus"
                      iconPosition="left"
                      onClick={() => setAddOpen(true)}
                    >
                      Add
                    </Button>
                  </div>
                  {(attributesError ?? hierarchyError) && (
                    <div className={styles.panelNotice}>
                      <div
                        className={styles.fieldError}
                        role="alert"
                        data-testid="space-attributes-error"
                      >
                        {attributesError ?? hierarchyError}
                      </div>
                    </div>
                  )}
                  {clientIdPinned && (
                    <div className={styles.panelNotice}>
                      <div
                        className={styles.constraintNote}
                        data-testid="client-id-pinned-note"
                      >
                        {clientIdPinUpgrades
                          ? clientIdPinUpgradeNote(clientIdName)
                          : clientIdPinNote(clientIdName)}
                      </div>
                    </div>
                  )}
                  {pendingRemove && isClientIdRemoval && !clientIdPinned && (
                    <div className={styles.panelNotice}>
                      <div className={styles.removeBanner} role="alert">
                        <div className={styles.removeBannerTitle}>
                          Remove the {pendingRemove.name} attribute?
                        </div>
                        <p className={styles.removeBannerText}>
                          Removing the client attribute prevents user based restriction to
                          provisioned workspaces. Access to documents without a{" "}
                          {pendingRemove.name} value will be determined by which roles have
                          access to the space.
                        </p>
                        <div className={styles.removeBannerActions}>
                          <Button variant="neutral" onClick={() => setPendingRemove(null)}>
                            Keep {pendingRemove.name}
                          </Button>
                          <Button variant="danger" onClick={confirmRemove}>
                            Remove {pendingRemove.name}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  <div
                    className={
                      refGlobals.length === 0 ? styles.panelBody : styles.panelBodyFlush
                    }
                  >
                    {refGlobals.length === 0 ? (
                      <div className={styles.emptyState}>
                        <span className={styles.emptyIcon}>
                          <Icon type="badge-check" size="xx-large" />
                        </span>
                        <div>
                          No global attributes yet — use Add above to reference one from the
                          shared library.
                        </div>
                      </div>
                    ) : (
                      <SpaceGlobalsGrid
                        rows={refGlobals.map((a) => ({
                          key: a.key,
                          name: a.name,
                          dataType: a.dataType,
                          captures: a.captures,
                          required: !!draft.globals[a.key].required,
                          default: draft.globals[a.key].default || "",
                          values: a.values ?? null,
                          validates: a.validates ?? "",
                          assignmentLocked: clientIdPinned && a.key === clientIdKey,
                          requirementLockedReason: requirementLockedReasonFor(a),
                          removeLockedReason: removeLockedReasonFor(a),
                        }))}
                        onSetRequired={(key, v) => setGlobal(key, { required: v })}
                        onSetDefault={(key, v) => setGlobal(key, { default: v })}
                        onRemove={requestRemoveGlobal}
                      />
                    )}
                  </div>
                </div>

                {/* Space-local attributes */}
                <div className={styles.panel}>
                  <div className={styles.panelHead}>
                    <h3 className={styles.panelHeadTitle}>Space-local attributes</h3>
                    <Button
                      variant="primary"
                      icon="plus"
                      iconPosition="left"
                      onClick={() => setEditingLocal({ mode: "add", attr: null })}
                    >
                      Create
                    </Button>
                  </div>
                  <div className={styles.panelBody}>
                    {draft.locals.length === 0 ? (
                      <div className={styles.emptyState}>
                        <span className={styles.emptyIcon}>
                          <Icon type="pen-to-square" size="xx-large" />
                        </span>
                        <div>
                          No local attributes yet — use Create above to add one scoped to this
                          space.
                        </div>
                      </div>
                    ) : (
                      <table className={styles.latTable}>
                        <thead>
                          <tr>
                            <th className={styles.latHead}>Attribute</th>
                            <th className={styles.latHead}>Data type</th>
                            <th className={styles.latHead}>Requirement</th>
                            <th className={styles.latHead}>Options</th>
                            <th className={styles.latHead} aria-label="Actions" />
                          </tr>
                        </thead>
                        <tbody>
                          {draft.locals.map((f) => {
                            const summary =
                              isSelectType(f.dataType) && f.options && f.options.length
                                ? f.options.join(", ")
                                : "—";
                            return (
                              <tr key={f.id}>
                                <td className={styles.latCell}>
                                  {f.name || "Untitled attribute"}
                                </td>
                                <td className={`${styles.latCell} ${styles.latMuted}`}>
                                  {f.dataType}
                                </td>
                                <td className={styles.latCell}>
                                  <span
                                    className={
                                      f.required ? styles.reqTag.req : styles.reqTag.opt
                                    }
                                  >
                                    {f.required ? "Required" : "Optional"}
                                  </span>
                                </td>
                                <td className={`${styles.latCell} ${styles.latMuted}`}>
                                  {summary}
                                </td>
                                <td className={styles.latCell}>
                                  <div className={styles.latActs}>
                                    <button
                                      className={styles.iconBtn}
                                      aria-label="Edit attribute"
                                      onClick={() => {
                                        const clickMode = persistedLocalIds.current.has(f.id)
                                          ? "edit"
                                          : "add";
                                        setEditingLocal({ mode: clickMode, attr: f });
                                      }}
                                      type="button"
                                    >
                                      <Icon type="pen-to-square" size="small" />
                                    </button>
                                    <button
                                      className={styles.iconBtn}
                                      aria-label="Remove attribute"
                                      onClick={() => requestRemoveLocal(f.id, f.name)}
                                      type="button"
                                    >
                                      <Icon type="trash" size="small" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {showPreview && (
              <div>
                <LivePreview draft={draft} globalLib={globalLib} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.foot}>
        <Button variant="neutral" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          icon="check"
          iconPosition="left"
          disabled={!canSave || saving}
          onClick={() => void save()}
        >
          Save space
        </Button>
      </div>

      {addOpen && (
        <ModalV2
          show
          onHide={() => setAddOpen(false)}
          size="medium"
          title="Add global attributes"
        >
          <ModalV2.Body>
            <div className={styles.checkList}>
              {sortGlobalsByRolePriority([...globalLib], globalLib).map((a) => {
                const referenced = !!draft.globals[a.key]?.on;
                const isDefault = a.key === clientIdKey;
                const missingParentNames = referenceAncestorKeys(a.key, globalLib)
                  .filter((parentKey) => !draft.globals[parentKey]?.on)
                  .map(
                    (parentKey) =>
                      globalLib.find((attribute) => attribute.key === parentKey)?.name ??
                      parentKey,
                  );
                const hierarchyDisabled = missingParentNames.length > 0;
                const descendantLockedReason = referenced ? removeLockedReasonFor(a) : null;
                return (
                  <div className={isDefault ? styles.gamRowDefault : styles.gamRow} key={a.key}>
                    <Checkbox
                      id={`gattr-${a.key}`}
                      checked={referenced}
                      disabled={
                        (isDefault && referenced) ||
                        (hierarchyDisabled && !referenced) ||
                        !!descendantLockedReason
                      }
                      onChange={() => toggleGlobalFromPicker(a.key)}
                      label={
                        <span className={styles.gamLabel}>
                          <span className={styles.gamNameLine}>
                            <span className={styles.gamName}>{a.name}</span>
                            <Tag variant="neutral" label={a.dataType} />
                            {isDefault && (
                              <span className={styles.gamDefaultTag}>
                                {referenced && <Icon type="lock" size="small" />}
                                Default
                              </span>
                            )}
                          </span>
                          <span className={styles.gamCap}>
                            {descendantLockedReason
                              ? descendantLockedReason
                              : hierarchyDisabled
                                ? `Requires ${missingParentNames.join(" and ")}`
                                : isDefault
                                  ? clientIdPinned
                                    ? clientIdPinNote(a.name)
                                    : referenced
                                      ? "On by default for every space — to remove it, use the referenced list below."
                                      : "A default attribute for every space — check to add it back."
                                  : a.captures}
                          </span>
                        </span>
                      }
                    />
                  </div>
                );
              })}
            </div>
          </ModalV2.Body>
          <ModalV2.Footer>
            <div className={styles.modalFooter}>
              <Button variant="primary" onClick={() => setAddOpen(false)}>
                Done
              </Button>
            </div>
          </ModalV2.Footer>
        </ModalV2>
      )}

      {editingLocal && (
        <LocalModal
          mode={editingLocal.mode}
          initial={editingLocal.attr}
          globalLib={globalLib}
          siblings={draft.locals}
          savedOptions={
            editingLocal.attr
              ? (persistedLocalOptions.current.get(editingLocal.attr.id) ?? [])
              : []
          }
          onCommit={commitLocal}
          onClose={() => setEditingLocal(null)}
        />
      )}

      {pendingRemove && !isClientIdRemoval && (
        <ModalV2
          show
          onHide={closeRemoveConfirmation}
          size="medium"
          title={`Remove “${pendingRemove.name}” from this space?`}
        >
          <ModalV2.Body>
            <div className={styles.removeModalBody}>
              <div className={styles.removeBanner} role="alert">
                <div className={styles.removeBannerTitle}>Forward-looking only</div>
                <p className={styles.removeBannerText}>
                  Removing this reference takes the attribute out of filters, table columns, and
                  the upload form for this space going forward. The attribute itself is
                  untouched — documents that already have it keep their values, and it stays
                  available via APIs and MCP automation.
                </p>
              </div>
              {removeImpact.status === "loading" && (
                <div className={styles.removeModalCount}>Checking affected documents…</div>
              )}
              {removeImpact.status === "ready" && (
                <div className={styles.removeModalCount}>
                  <strong>{removeImpact.count}</strong>{" "}
                  {removeImpact.count === 1 ? "document" : "documents"} in this space currently{" "}
                  {removeImpact.count === 1 ? "has" : "have"} a value for{" "}
                  <strong>{pendingRemove.name}</strong>.
                </div>
              )}
              {removeImpact.status === "unavailable" && (
                <div className={styles.removeModalCount} role="alert">
                  The affected document count is unavailable.
                  <Button
                    variant="neutral"
                    onClick={() => void loadRemoveImpact(pendingRemove.key)}
                  >
                    Retry
                  </Button>
                </div>
              )}
            </div>
          </ModalV2.Body>
          <ModalV2.Footer>
            <div className={styles.modalFooter}>
              <Button variant="neutral" onClick={closeRemoveConfirmation}>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmRemove}>
                Remove attribute
              </Button>
            </div>
          </ModalV2.Footer>
        </ModalV2>
      )}
    </div>
  );
}
